import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  FileText, Shield, Globe, Award, Users, BookOpen, CheckCircle, Star, Briefcase, GraduationCap,
  Heart, Activity, Syringe, User, Brain, Stethoscope, Pill
} from 'lucide-react';
import { trainingModules } from '../data/trainingData';
import { graphqlService } from '../services/graphqlService';

const ModuleContext = createContext();

export const useModules = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
};

// Map icon names to React components
const iconMap = {
  'FileText': FileText,
  'Shield': Shield,
  'Globe': Globe,
  'Award': Award,
  'Users': Users,
  'BookOpen': BookOpen,
  'CheckCircle': CheckCircle,
  'Star': Star,
  'Briefcase': Briefcase,
  'GraduationCap': GraduationCap,
  'Heart': Heart,
  'Activity': Activity,
  'Syringe': Syringe,
  'User': User,
  'Brain': Brain,
  'Stethoscope': Stethoscope,
  'Pill': Pill
};

// Helper function to convert icon React element to string
const getIconString = (icon) => {
  // If it's already a string, return it
  if (typeof icon === 'string') {
    return icon;
  }
  
  // If it's a React element, try to extract the component name
  if (React.isValidElement(icon)) {
    const componentName = icon.type?.displayName || icon.type?.name || icon.type;
    // Check if it's a known icon component
    if (typeof componentName === 'string' && iconMap[componentName]) {
      return componentName;
    }
    // Try to get from props or type
    if (typeof componentName === 'function') {
      const funcName = componentName.name;
      if (iconMap[funcName]) {
        return funcName;
      }
    }
  }
  
  // Default to BookOpen
  return 'BookOpen';
};

// Helper function to convert icon string to React component
const getIconComponent = (icon) => {
  // If it's already a valid React element, return it
  if (React.isValidElement(icon)) {
    return icon;
  }
  
  // If it's a string, convert to component
  if (typeof icon === 'string' && iconMap[icon]) {
    const IconComponent = iconMap[icon];
    return <IconComponent size={32} color="#0477aa" />;
  }
  
  // Default to BookOpen if icon is not found
  return <BookOpen size={32} color="#0477aa" />;
};

// Helper function to recursively remove __typename from objects and arrays
const removeTypename = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeTypename(item));
  }
  
  if (typeof obj === 'object') {
    const cleaned = {};
    for (const key in obj) {
      if (key !== '__typename') {
        cleaned[key] = removeTypename(obj[key]);
      }
    }
    return cleaned;
  }
  
  return obj;
};

// Helper function to restore icons from original trainingModules
const restoreIcons = (modules) => {
  return modules.map(module => {
    // Find the original module to get its icon
    const originalModule = trainingModules.find(m => m.id === module.id);
    
    // Check if current icon is a valid React element
    const currentIcon = module.icon;
    const isIconValid = React.isValidElement(currentIcon);
    
    // Priority: original icon > valid current icon > convert string to component > default
    if (originalModule?.icon) {
      return { ...module, icon: originalModule.icon };
    } else if (isIconValid) {
      return { ...module, icon: currentIcon };
    } else {
      return { ...module, icon: getIconComponent(currentIcon) };
    }
  });
};

export const ModuleProvider = ({ children }) => {
  const [modules, setModules] = useState(trainingModules);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load modules from GraphQL on mount
    const loadModules = async () => {
      setLoading(true);
      try {
        const modules = await graphqlService.getAllModules();
        const modulesWithIcons = restoreIcons(modules);
        setModules(modulesWithIcons);
      } catch (error) {
        console.error('Error loading modules from GraphQL:', error);
        // Fallback to default modules if GraphQL fails
        setModules(trainingModules);
      } finally {
        setLoading(false);
      }
    };
    
    loadModules();
  }, []);

  const saveModules = async (updatedModules) => {
    // Restore icons before setting state
    const modulesWithIcons = restoreIcons(updatedModules);
    setModules(modulesWithIcons);
    // Save to GraphQL - update each module
    setLoading(true);
    try {
      await Promise.all(
        updatedModules.map(module => {
          const { _id, id, createdAt, updatedAt, icon, ...input } = module;
          
          // Remove __typename from nested objects
          const cleanedContent = removeTypename(input.content || { sections: [] });
          const cleanedQuiz = removeTypename(input.quiz || []);
          const cleanedAssignments = removeTypename(input.assignments || {});
          
          // Clean input and convert icon to string
          const cleanInput = {
            ...input,
            icon: getIconString(icon),
            duration: typeof input.duration === 'number' ? input.duration : parseInt(input.duration, 10) || 30,
            tags: Array.isArray(input.tags) ? input.tags : [],
            category: input.category || '',
            content: cleanedContent,
            quiz: cleanedQuiz,
            assignments: cleanedAssignments
          };
          return graphqlService.updateModule(id, cleanInput).catch(err => {
            console.error(`Error updating module ${id}:`, err);
          });
        })
      );
    } catch (error) {
      console.error('Error saving modules:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to notify users when a module is assigned to them
  const notifyAssignedUsers = async (module, previousAssignments = null) => {
    try {
      // Get all users from GraphQL
      const users = await graphqlService.getAllUsers();
      
      const assignments = module.assignments || { roles: [], users: [], departments: [], designations: [], specializations: [] };
      const prevAssignments = previousAssignments || { roles: [], users: [], departments: [], designations: [], specializations: [] };
      
      // Find users who should receive notifications
      const usersToNotify = users.filter(user => {
        // Check if user matches any assignment criteria
        const matchesRole = assignments.roles.includes(user.role);
        const matchesUser = assignments.users.includes(user.email);
        const matchesDepartment = assignments.departments.includes(user.department);
        const matchesDesignation = user.designation && assignments.designations.includes(user.designation);
        const matchesSpecialization = user.specialization && assignments.specializations.includes(user.specialization);
        
        // Check if this is a new assignment (wasn't assigned before)
        const wasAssigned = 
          prevAssignments.roles.includes(user.role) ||
          prevAssignments.users.includes(user.email) ||
          prevAssignments.departments.includes(user.department) ||
          (user.designation && prevAssignments.designations.includes(user.designation)) ||
          (user.specialization && prevAssignments.specializations.includes(user.specialization));
        
        const isNowAssigned = matchesRole || matchesUser || matchesDepartment || matchesDesignation || matchesSpecialization;
        
        // Notify if newly assigned
        return isNowAssigned && !wasAssigned;
      });
      
      // Add notifications for each affected user via GraphQL
      await Promise.all(
        usersToNotify.map(user =>
          graphqlService.createNotification({
            userEmail: user.email,
            title: 'New Training Module Assigned',
            message: `A new training module "${module.title}" has been assigned to you. Check it out in your Dashboard.`,
            type: 'info',
            moduleId: module.id
          }).catch(err => {
            console.error(`Error creating notification for ${user.email}:`, err);
          })
        )
      );
      
      // Trigger event to reload notifications for logged-in users
      window.dispatchEvent(new Event('notificationsUpdated'));
    } catch (error) {
      console.error('Error notifying users about module assignment:', error);
    }
  };

  const createModule = async (moduleData) => {
    setLoading(true);
    try {
      // Remove __typename from nested objects
      const cleanedContent = removeTypename(moduleData.content || { sections: [] });
      const cleanedQuiz = removeTypename(moduleData.quiz || []);
      const cleanedAssignments = removeTypename(moduleData.assignments || {});
      
      const input = {
        id: moduleData.id || `module-${Date.now()}`,
        title: moduleData.title,
        description: moduleData.description,
        duration: typeof moduleData.duration === 'number' ? moduleData.duration : parseInt(moduleData.duration, 10) || 30,
        difficulty: moduleData.difficulty,
        icon: getIconString(moduleData.icon),
        category: moduleData.category || '',
        tags: Array.isArray(moduleData.tags) ? moduleData.tags : [],
        assignments: {
          roles: cleanedAssignments.roles || [],
          users: cleanedAssignments.users || [],
          departments: cleanedAssignments.departments || [],
          designations: cleanedAssignments.designations || [],
          specializations: cleanedAssignments.specializations || []
        },
        content: cleanedContent,
        quiz: cleanedQuiz
      };
      
      const newModule = await graphqlService.createModule(input);
      const modulesWithIcons = restoreIcons([...modules, newModule]);
      setModules(modulesWithIcons);
      
      // Notify users about the new module assignment
      await notifyAssignedUsers(newModule);
      
      setLoading(false);
      return newModule;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const updateModule = async (moduleId, moduleData) => {
    setLoading(true);
    try {
      const existingModule = modules.find(m => m.id === moduleId);
      const previousAssignments = existingModule?.assignments || { roles: [], users: [], departments: [], designations: [], specializations: [] };
      
      // Clean module data - remove invalid fields and convert types
      const { _id, id, createdAt, updatedAt, ...cleanData } = moduleData;
      
      // Remove __typename from nested objects
      const cleanedContent = removeTypename(cleanData.content || existingModule?.content || { sections: [] });
      const cleanedQuiz = removeTypename(cleanData.quiz || existingModule?.quiz || []);
      const cleanedAssignments = removeTypename(cleanData.assignments || existingModule?.assignments || {});
      
      const input = {
        title: cleanData.title,
        description: cleanData.description,
        duration: typeof cleanData.duration === 'number' ? cleanData.duration : parseInt(cleanData.duration, 10) || 30,
        difficulty: cleanData.difficulty,
        icon: getIconString(cleanData.icon),
        category: cleanData.category || '',
        tags: Array.isArray(cleanData.tags) ? cleanData.tags : [],
        assignments: {
          roles: cleanedAssignments.roles || existingModule?.assignments?.roles || [],
          users: cleanedAssignments.users || existingModule?.assignments?.users || [],
          departments: cleanedAssignments.departments || existingModule?.assignments?.departments || [],
          designations: cleanedAssignments.designations || existingModule?.assignments?.designations || [],
          specializations: cleanedAssignments.specializations || existingModule?.assignments?.specializations || []
        },
        content: cleanedContent,
        quiz: cleanedQuiz
      };
      
      const updatedModule = await graphqlService.updateModule(moduleId, input);
      const modulesWithIcons = restoreIcons(modules.map(m => m.id === moduleId ? updatedModule : m));
      setModules(modulesWithIcons);
      
      // Notify users about new assignments (only if assignments changed)
      await notifyAssignedUsers(updatedModule, previousAssignments);
      
      setLoading(false);
      return updatedModule;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const deleteModule = async (moduleId) => {
    setLoading(true);
    try {
      await graphqlService.deleteModule(moduleId);
      const updatedModules = modules.filter(module => module.id !== moduleId);
      const modulesWithIcons = restoreIcons(updatedModules);
      setModules(modulesWithIcons);
      setLoading(false);
      return true;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const getModule = (moduleId) => {
    return modules.find(module => module.id === moduleId);
  };

  const getModulesByDifficulty = (difficulty) => {
    return modules.filter(module => module.difficulty === difficulty);
  };

  const searchModules = (query) => {
    const lowercaseQuery = query.toLowerCase();
    return modules.filter(module => 
      module.title.toLowerCase().includes(lowercaseQuery) ||
      module.description.toLowerCase().includes(lowercaseQuery)
    );
  };

  const getModulesForUser = async (user) => {
    if (!user) return [];
    
    try {
      const modules = await graphqlService.getModulesForUser(user);
      const modulesWithIcons = restoreIcons(modules);
      return modulesWithIcons;
    } catch (error) {
      console.error('Error fetching modules for user:', error);
      // Fallback to local filtering
      const filteredModules = modules.filter(module => {
        const assignments = module.assignments || { roles: [], users: [], departments: [], designations: [], specializations: [] };
        
        const roles = Array.isArray(assignments.roles) ? assignments.roles : [];
        const users = Array.isArray(assignments.users) ? assignments.users : [];
        const departments = Array.isArray(assignments.departments) ? assignments.departments : [];
        const designations = Array.isArray(assignments.designations) ? assignments.designations : [];
        const specializations = Array.isArray(assignments.specializations) ? assignments.specializations : [];
        
        const assignedToRole = roles.includes(user.role);
        const assignedToUser = users.includes(user.email);
        const assignedToDepartment = departments.includes(user.department);
        const assignedToDesignation = user.designation && designations.includes(user.designation);
        const assignedToSpecialization = user.specialization && specializations.includes(user.specialization);
        const hasNoAssignments = roles.length === 0 && users.length === 0 && departments.length === 0 &&
                                designations.length === 0 && specializations.length === 0;
        
        return assignedToRole || assignedToUser || assignedToDepartment || assignedToDesignation || assignedToSpecialization || hasNoAssignments;
      });
      
      return restoreIcons(filteredModules);
    }
  };

  const getModulesByAssignment = (assignmentType, assignmentValue) => {
    return modules.filter(module => {
      const assignments = module.assignments || { roles: [], users: [], departments: [], designations: [], specializations: [] };
      const assignmentArray = assignments[assignmentType];
      return Array.isArray(assignmentArray) && assignmentArray.includes(assignmentValue);
    });
  };

  const getModuleStats = async () => {
    try {
      return await graphqlService.getModuleStats();
    } catch (error) {
      console.error('Error fetching module stats:', error);
      // Fallback to local calculation
      const totalModules = modules.length;
      const modulesByDifficulty = modules.reduce((acc, module) => {
        acc[module.difficulty] = (acc[module.difficulty] || 0) + 1;
        return acc;
      }, {});
      
      const totalSections = modules.reduce((acc, module) => 
        acc + (module.content?.sections?.length || 0), 0
      );
      
      const totalQuizQuestions = modules.reduce((acc, module) => 
        acc + (module.quiz?.length || 0), 0
      );

      return {
        totalModules,
        modulesByDifficulty,
        totalSections,
        totalQuizQuestions,
        averageSectionsPerModule: totalModules > 0 ? Math.round(totalSections / totalModules) : 0,
        averageQuizQuestionsPerModule: totalModules > 0 ? Math.round(totalQuizQuestions / totalModules) : 0
      };
    }
  };

  const value = {
    modules,
    loading,
    createModule,
    updateModule,
    deleteModule,
    getModule,
    getModulesByDifficulty,
    searchModules,
    getModulesForUser,
    getModulesByAssignment,
    getModuleStats,
    saveModules
  };

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
};
